import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import type { SimulationState } from "@/hooks/useSimulator";

interface Props {
  simulation: SimulationState;
}

const CYCLE_INFO: Record<string, { label: string; color: string; description: string }> = {
  expansion: { label: "Expansion", color: "text-emerald-400", description: "Growing economy, rising profits" },
  peak: { label: "Peak", color: "text-amber-400", description: "Market at highs, caution advised" },
  recession: { label: "Recession", color: "text-red-400", description: "Declining activity, fear rising" },
  recovery: { label: "Recovery", color: "text-blue-400", description: "Market bottoming, opportunities emerging" },
};

const EconomicIndicators = ({ simulation: sim }: Props) => {
  const cycle = CYCLE_INFO[sim.market_cycle] || CYCLE_INFO.expansion;
  const sentimentAngle = (sim.sentiment / 100) * 180 - 90;
  const hw500Data = sim.hw500_history.map((h) => ({ round: h.round, level: h.level }));

  const recentHistory = sim.price_history.slice(-5);
  let volatilityIndex = 0;
  if (recentHistory.length >= 2) {
    const changes: number[] = [];
    for (let i = 1; i < recentHistory.length; i++) {
      const prev = recentHistory[i - 1].prices;
      const curr = recentHistory[i].prices;
      for (const ticker of Object.keys(curr)) {
        if (prev[ticker]) {
          changes.push(Math.abs((curr[ticker] - prev[ticker]) / prev[ticker]) * 100);
        }
      }
    }
    volatilityIndex = changes.length > 0 ? changes.reduce((s, v) => s + v, 0) / changes.length : 0;
  }

  const volLevel =
    volatilityIndex < 2 ? { label: "Low", color: "text-emerald-400" } :
    volatilityIndex < 5 ? { label: "Moderate", color: "text-amber-400" } :
    { label: "High", color: "text-red-400" };

  const hw500Start = sim.hw500_history[0]?.level || sim.hw500_level;
  const hw500Return = ((sim.hw500_level - hw500Start) / hw500Start) * 100;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-heading font-bold text-sm text-foreground/90 mb-1">Economic Indicators</h3>
        <p className="text-muted-foreground text-[10px]">Market conditions and cycle analysis</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
          <p className="text-muted-foreground text-[10px] mb-1">Market Cycle</p>
          <p className={`font-heading font-bold text-lg ${cycle.color}`}>{cycle.label}</p>
          <p className="text-muted-foreground/50 text-[10px] mt-0.5">{cycle.description}</p>
          <div className="flex gap-1 mt-2">
            {["recovery", "expansion", "peak", "recession"].map((phase) => (
              <div
                key={phase}
                className={`flex-1 h-1.5 rounded-full transition-all ${
                  sim.market_cycle === phase ? "bg-accent" : "bg-muted/50"
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[8px] text-muted-foreground/30 mt-0.5">
            <span>Recovery</span>
            <span>Expansion</span>
            <span>Peak</span>
            <span>Recession</span>
          </div>
        </div>

        <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
          <p className="text-muted-foreground text-[10px] mb-1">Fear & Greed</p>
          <div className="relative mx-auto w-24 h-14 mt-1">
            <svg viewBox="0 0 100 55" className="w-full h-full">
              <defs>
                <linearGradient id="gauge-grad" x1="0%" x2="100%">
                  <stop offset="0%" stopColor="#f87171" />
                  <stop offset="25%" stopColor="#fb923c" />
                  <stop offset="50%" stopColor="#fbbf24" />
                  <stop offset="75%" stopColor="#a3e635" />
                  <stop offset="100%" stopColor="#34d399" />
                </linearGradient>
              </defs>
              <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="url(#gauge-grad)" strokeWidth="6" strokeLinecap="round" />
              <line
                x1="50"
                y1="50"
                x2={50 + 30 * Math.cos((sentimentAngle * Math.PI) / 180)}
                y2={50 - 30 * Math.sin((sentimentAngle * Math.PI) / 180)}
                stroke="hsl(var(--foreground))"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="50" cy="50" r="3" fill="hsl(var(--foreground))" />
            </svg>
          </div>
          <p className="text-center font-heading font-bold text-lg text-foreground">{sim.sentiment}</p>
          <p className="text-center text-[10px] text-muted-foreground">
            {sim.sentiment < 25 ? "Extreme Fear" : sim.sentiment < 45 ? "Fear" : sim.sentiment < 55 ? "Neutral" : sim.sentiment < 75 ? "Greed" : "Euphoria"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
          <p className="text-muted-foreground text-[10px] mb-1">Volatility Index</p>
          <p className={`font-heading font-bold text-lg ${volLevel.color}`}>{volatilityIndex.toFixed(1)}%</p>
          <p className={`text-[10px] ${volLevel.color}`}>{volLevel.label} volatility</p>
          <div className="mt-2 h-1.5 rounded-full bg-muted/50 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                volatilityIndex < 2 ? "bg-emerald-400/60" : volatilityIndex < 5 ? "bg-amber-400/60" : "bg-red-400/60"
              }`}
              style={{ width: `${Math.min(volatilityIndex * 10, 100)}%` }}
            />
          </div>
        </div>

        <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
          <p className="text-muted-foreground text-[10px] mb-1">HW500 Return</p>
          <p className={`font-heading font-bold text-lg ${hw500Return >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {hw500Return >= 0 ? "+" : ""}{hw500Return.toFixed(2)}%
          </p>
          <p className="text-muted-foreground/50 text-[10px]">{sim.hw500_level.toFixed(1)} pts</p>
        </div>
      </div>

      {hw500Data.length > 1 && (
        <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
          <p className="text-muted-foreground text-[10px] mb-2">HW500 Index Trend</p>
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={hw500Data}>
                <XAxis dataKey="round" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v: number) => [v.toFixed(1), "HW500"]}
                />
                <Line type="monotone" dataKey="level" stroke="hsl(195, 70%, 65%)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default EconomicIndicators;
